import { Controller } from "@hotwired/stimulus";
import { FetchRequest } from "@rails/request.js";

export default class extends Controller {
  static targets = [
    "textField",
    "player",
    "tempUrl",
    "generateButton",
    "regenerate",
    "audioPreview",
  ];

  textFieldTargetConnected() {
    this.updateGenerateButton();
    if (this.hasRegenerateTarget) {
      this.originalText = this.textFieldTarget.value;
    }
    this.textFieldTarget.addEventListener("input", this.handleTextChange);
  }

  textFieldTargetDisconnected() {
    this.textFieldTarget.removeEventListener("input", this.handleTextChange);
  }

  handleTextChange = () => {
    this.updateGenerateButton();
    if (!this.hasRegenerateTarget) return;
    const changed = this.textFieldTarget.value !== this.originalText;
    this.regenerateTarget.hidden = !changed;
  };

  containsPlaceholders() {
    return /\[\[.+?\]\]/.test(this.textFieldTarget.value);
  }

  updateGenerateButton() {
    if (!this.hasGenerateButtonTarget) return;
    this.generateButtonTarget.disabled = this.containsPlaceholders();
  }

  async generateTempUrlBeforeSubmit(event) {
    const currentText = this.textFieldTarget.value;

    // If we've already generated the preview for this text, allow submission
    if (this.data.has("previewText") && this.data.get("previewText") == currentText) {
      this.data.delete("previewText");
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    await this.generatePreview();

    const form = this.textFieldTarget.closest("form");
    form.requestSubmit();
  }

  async previewSpokenText(event) {
    event.preventDefault();
    event.stopPropagation();
    const link = event.target;
    if (link.classList.contains("disabled")) return;

    link.classList.add("disabled");
    await this.generatePreview();
    link.classList.remove("disabled");
    this.playPreview();
  }

  async generatePreview() {
    const rawText = this.textFieldTarget.value;
    this.data.set("previewText", rawText);
    if (rawText.length == 0) return;

    const request = new FetchRequest("POST", this.data.get("polly-url"), {
      body: JSON.stringify({ text: rawText }),
    });
    const response = await request.perform();
    const data = await response.json;

    if (this.hasTempUrlTarget) {
      this.tempUrlTarget.value = data.url;
    }
  }

  async generateAndSave(event) {
    event.preventDefault();
    const button = event.currentTarget;
    button.disabled = true;
    button.innerHTML = '<i class="spinner loading icon"></i> Generating Audio...';

    const rawText = this.textFieldTarget.value;
    if (rawText.length == 0) {
      button.disabled = false;
      button.innerHTML = '<i class="volume up icon"></i> Generate Audio';
      return;
    }

    const request = new FetchRequest("POST", this.data.get("polly-url"), {
      body: JSON.stringify({
        text: rawText,
        notification_id: this.data.get("notification-id"),
      }),
    });
    await request.perform();
    // Result arrives via Turbo broadcast — notification updates wizard step
  }

  async regenerateAudio(event) {
    event.preventDefault();
    const button = event.currentTarget;
    button.disabled = true;
    button.innerHTML = '<i class="spinner loading icon"></i> Generating Audio...';

    if (this.hasAudioPreviewTarget) {
      this.audioPreviewTarget.innerHTML = "";
    }

    const rawText = this.textFieldTarget.value;
    const request = new FetchRequest("POST", this.data.get("polly-url"), {
      body: JSON.stringify({
        text: rawText,
        notification_id: this.data.get("notification-id"),
      }),
    });
    await request.perform();
  }

  playPreview() {
    if (!this.hasTempUrlTarget || !this.tempUrlTarget.value) return;

    this.playerTarget.innerHTML = "";
    const audio = document.createElement("audio");
    audio.src = this.tempUrlTarget.value;
    this.playerTarget.appendChild(audio);
    audio.play();
  }
}
